/**
 * @file src/utils/storage.js
 * @class PlatformStorage
 */
class PlatformStorage {
  /**
   * 🛡️ STATIC KEYS
   * Standard 18: Prevents "Magic String" bugs.
   */
  static KEYS = {
    ACCESS_TOKEN: 'accessToken',
    REFRESH_TOKEN: 'refreshToken',
    TRACE_ID: 'last_trace_id',
    USER_ID: 'user_id',
    COOKIE_CONSENT: 'moksy_cookie_consent',
    ONBOARDING_PENDING: 'onboarding_pending',
    ONBOARDING_COMPLETED_AT: 'onboarding_completed_at',
    IMPERSONATION_SESSION: 'impersonation_session',
    // 🔙 The operator's own session, stashed while they "Test as" somebody
    // else so they have a way back. Dev tooling only — see
    // components/blocks/test-persona-return.block.jsx.
    TEST_PERSONA_RETURN: 'test_persona_return',
    IMPERSONATION_RETURN_TO: 'impersonation_return_to',
    // "Remember me" preference. Non-sensitive boolean flag (lives in
    // localStorage) that decides WHERE the session keys below are stored.
    REMEMBER_ME: 'auth_remember',
    // Tab-scoped (sessionStorage) mark set when this tab's identity was found
    // to disagree with its token. A detached tab is nobody until somebody signs
    // in HERE: it never adopts the remembered token another tab wrote.
    TAB_DETACHED: 'auth_tab_detached'
  };

  /**
   * 🔐 STORAGE POLICY
   * Auth tokens are session-scoped BY DEFAULT (sessionStorage) so they never
   * outlive the browser session — closing the browser signs the user out.
   *
   * When the user ticks "Remember me" at login, `setRemember(true)` flips the
   * preference and these same keys are instead kept in localStorage so the
   * session survives a browser restart. Because the routing is driven by the
   * sticky flag (not by the individual call site), later silent token refreshes
   * automatically write to the same store — the choice stays consistent for the
   * whole session, not just the first write.
   *
   * Either way exactly ONE copy is kept: `set()`/`get()` clear the value from
   * the other store so a stale token can't shadow the live one or linger after
   * a restart. Legacy localStorage values are migrated lazily on first read.
   */
  static SESSION_KEYS = new Set([
    PlatformStorage.KEYS.ACCESS_TOKEN,
    PlatformStorage.KEYS.REFRESH_TOKEN,
    PlatformStorage.KEYS.TRACE_ID,
    PlatformStorage.KEYS.USER_ID,
    // 🪪 IDENTITY FOLLOWS THE SESSION IT BELONGS TO.
    //
    // ONBOARDING_PENDING carries `profile.email`. It was written to
    // localStorage while the tokens beside it were session-scoped, so closing
    // the browser did exactly what the policy above promises for tokens and
    // the opposite for the email: the session died, the identity persisted.
    // Walked 2026-09-08 — an anonymous guest interview, no account, no token,
    // and `admin-1@moksy.local` sitting in localStorage from whoever used the
    // browser last. It is only cleared by that same person logging back in and
    // completing onboarding, so an account abandoned mid-onboarding leaves its
    // email in that browser indefinitely.
    //
    // An impersonation session outliving the browser is the same bug with
    // more authority behind it.
    PlatformStorage.KEYS.ONBOARDING_PENDING,
    PlatformStorage.KEYS.IMPERSONATION_SESSION,
    PlatformStorage.KEYS.IMPERSONATION_RETURN_TO,
    PlatformStorage.KEYS.TEST_PERSONA_RETURN
  ]);

  // Identity-bearing session keys that are NOT the token. A stale localStorage
  // copy of one of these is somebody else's data, never a usable credential,
  // so it is always safe to drop — unlike a token, where localStorage may hold
  // another person's legitimately remembered session.
  static #PURGEABLE = [
    PlatformStorage.KEYS.ONBOARDING_PENDING,
    PlatformStorage.KEYS.IMPERSONATION_SESSION,
    PlatformStorage.KEYS.IMPERSONATION_RETURN_TO,
    PlatformStorage.KEYS.TEST_PERSONA_RETURN
  ];

  /**
   * 🧹 Drop identity left in localStorage by a build that stored it there.
   *
   * Adding a key to SESSION_KEYS changes where it is WRITTEN; it does nothing
   * about the copies already on people's machines, which would simply become
   * unreadable and stay. Called once at boot.
   */
  static purgeStaleIdentity() {
    const local = this.#getLocalStorage();
    if (!local) return 0;
    let dropped = 0;
    for (const key of PlatformStorage.#PURGEABLE) {
      try {
        if (local.getItem(key) == null) continue;
        // Only when this store is not where the key belongs right now.
        if (this.#storageForKey(key) === local) continue;
        local.removeItem(key);
        dropped += 1;
      } catch { /* storage blocked; nothing to purge */ }
    }
    return dropped;
  }

  // Whether "Remember me" is active. Read from localStorage so it persists
  // across restarts (it's the very thing that decides persistence).
  static #rememberEnabled() {
    try {
      return this.#getLocalStorage()?.getItem(PlatformStorage.KEYS.REMEMBER_ME) === '1';
    } catch { return false; }
  }

  /**
   * Set the "Remember me" preference, then RE-HOME any already-stored session
   * values into the now-correct store so the choice takes effect immediately
   * for tokens written earlier in the same login flow.
   */
  static setRemember(on) {
    const ls = this.#getLocalStorage();
    if (ls) {
      if (on) ls.setItem(PlatformStorage.KEYS.REMEMBER_ME, '1');
      else ls.removeItem(PlatformStorage.KEYS.REMEMBER_ME);
    }
    // Re-home only what is OURS: the values this tab holds in sessionStorage.
    // Never read localStorage here — before a login writes its own token, the
    // only token on the machine may be another person's remembered one, and
    // re-homing it would pull their identity into this tab and sign them out.
    const session = this.#getSessionStorage();
    if (!on || !session || session.getItem(PlatformStorage.KEYS.ACCESS_TOKEN) == null) return;
    this.SESSION_KEYS.forEach((key) => {
      const current = session.getItem(key);
      if (current == null) return;
      session.removeItem(key);          // so set() routes by the flag, not by "session holds it"
      this.set(key, current);
    });
  }

  static #getSessionStorage() {
    if (typeof window === 'undefined') return null;
    try { return window.sessionStorage; } catch { return null; }
  }

  static #getLocalStorage() {
    if (typeof window === 'undefined') return null;
    try { return window.localStorage; } catch { return null; }
  }

  static #storageForKey(key) {
    if (this.SESSION_KEYS.has(key)) {
      // 🪪 THIS TAB'S OWN SESSION WINS. `auth_remember` lives in localStorage,
      // which every tab on the machine shares. Walked 2026-09-05: a reviewer
      // signed in here WITHOUT "remember me" (token in sessionStorage); a
      // different person signed in with "remember me" in another tab; the flag
      // flipped to '1' for both tabs, this helper started reading
      // localStorage, and the reviewer's wallet page sent a withdrawal request
      // as the other person (V2.KYC_REQUIRED for THEIR id). A tab that holds
      // a session-scoped token is that person, whatever the shared flag says;
      // it never reads another store (platform law: one page, one identity).
      const session = this.#getSessionStorage();
      // A detached tab is nobody: it reads and writes only its own store.
      if (this.isDetached()) return session;
      if (session?.getItem(key) != null) return session;
      // The identity stamp (USER_ID) and the rest live WITH the token. Walked
      // 2026-09-05 as an admin (token in this tab's sessionStorage, no stamp
      // yet): another person signed in with "remember me" in another tab, the
      // stamp was read from localStorage — theirs — and the guard saw a
      // mismatch between MY token and THEIR stamp.
      if (session?.getItem(PlatformStorage.KEYS.ACCESS_TOKEN) != null) return session;
      // A remembered session on this machine is read by PRESENCE, not by the
      // shared flag. The flag flips at every login on the machine; when a
      // second person signed in here WITHOUT "remember me", the flag went off
      // and the first person's live localStorage token became unreadable —
      // a silent sign-out of somebody else. The flag now decides only where a
      // NEW token is written (see set()).
      const local = this.#getLocalStorage();
      if (local?.getItem(PlatformStorage.KEYS.ACCESS_TOKEN) != null) return local;
      return this.#rememberEnabled() ? local : session;
    }
    return this.#getLocalStorage();
  }

  // The store a session key should NOT live in, given the current preference —
  // used to drop the stale copy so only one source of truth remains.
  static #otherStorageForKey(key) {
    if (!this.SESSION_KEYS.has(key)) return null;
    const primary = this.#storageForKey(key);
    return primary === this.#getLocalStorage() ? this.#getSessionStorage() : this.#getLocalStorage();
  }

  static set(key, value) {
    if (!value) return this.remove(key);
    const serialized = typeof value === 'object' ? JSON.stringify(value) : String(value);
    // An explicit token write is a sign-in (or a deliberate switch) in THIS
    // tab: it re-attaches a tab that had been detached.
    if (key === PlatformStorage.KEYS.ACCESS_TOKEN) {
      try { this.#getSessionStorage()?.removeItem(PlatformStorage.KEYS.TAB_DETACHED); } catch { /* no session store */ }
    }
    // A NEW sign-in's token goes where the person asked: remembered →
    // localStorage, otherwise this tab. A refresh of a token this tab already
    // holds stays where it is. Every other session key follows the token.
    let primary;
    if (key === PlatformStorage.KEYS.ACCESS_TOKEN && this.#getSessionStorage()?.getItem(key) == null) {
      primary = this.#rememberEnabled() ? this.#getLocalStorage() : this.#getSessionStorage();
    } else {
      primary = this.#storageForKey(key);
    }
    primary?.setItem(key, serialized);

    // Keep one copy OF OURS: a remembered (localStorage) write clears this
    // tab's session copy so a stale token can't shadow the live one. A
    // session-scoped write never touches localStorage — that store may hold
    // ANOTHER person's remembered session on this machine, and "exactly one
    // copy" used to delete their identity stamp when this tab stamped its own
    // (walked 2026-09-05).
    if (this.SESSION_KEYS.has(key) && primary === this.#getLocalStorage()) {
      this.#getSessionStorage()?.removeItem(key);
    }
  }

  static get(key) {
    const primary = this.#storageForKey(key);
    let value = primary?.getItem(key) ?? null;

    // Adopt a value that lives in the other store — a legacy localStorage token,
    // or one written before the remember-me preference last changed — into the
    // correct store and drop the stale copy.
    // A tab with no session token of its own may adopt a remembered
    // (localStorage) token — that is what "remember me" means. It must NOT pull
    // a token OUT of another tab's sessionStorage (it cannot: sessionStorage is
    // per tab), and a tab that already holds its own token never gets here.
    // (Adoption of a remembered token happens in #storageForKey by presence;
    // a detached tab never gets there.)

    if (!value || value === 'undefined' || value === 'null') return null;
    try { return JSON.parse(value); } catch { return value; }
  }

  static isJwtExpired(token, skewSeconds = 30) {
    if (!token || typeof token !== 'string') return true;
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    try {
      const payload = JSON.parse(atob(parts[1]));
      const exp = Number(payload?.exp || 0);
      if (!exp) return true;
      const now = Math.floor(Date.now() / 1000);
      return now >= (exp - Math.max(0, Number(skewSeconds) || 0));
    } catch {
      return true;
    }
  }

  static hasUsableAccessToken() {
    const token = this.get(this.KEYS.ACCESS_TOKEN);
    if (!token || typeof token !== 'string') return false;
    return !this.isJwtExpired(token);
  }

  static clear() {
    Object.values(this.KEYS).forEach((key) => this.remove(key));
  }

  // Whether this tab was detached by an identity mismatch (see detach()).
  static isDetached() {
    try { return this.#getSessionStorage()?.getItem(PlatformStorage.KEYS.TAB_DETACHED) === '1'; } catch { return false; }
  }

  /**
   * 🪪 DETACH THIS TAB — and only this tab.
   *
   * The identity guard found the token and the stamp disagree. The old remedy
   * was remove(), which wipes BOTH stores: it signed the OTHER person out of
   * their remembered session too, and then, this tab holding nothing, get()
   * adopted whatever they wrote back — an admin's tab became a new joiner's
   * (walked 2026-09-05). Now: drop the session keys from the store this tab's
   * token lives in, mark the tab detached so it adopts nobody, and leave the
   * other store alone. Signing in here (set(ACCESS_TOKEN)) re-attaches.
   */
  static detach() {
    const own = this.#storageForKey(PlatformStorage.KEYS.ACCESS_TOKEN);
    this.SESSION_KEYS.forEach((key) => { try { own?.removeItem(key); } catch { /* ignore */ } });
    try { this.#getSessionStorage()?.setItem(PlatformStorage.KEYS.TAB_DETACHED, '1'); } catch { /* no session store */ }
  }

  static remove(key) {
    this.#getSessionStorage()?.removeItem(key);
    this.#getLocalStorage()?.removeItem(key);
  }
}

export { PlatformStorage as storage };
